// components/InquiryPops/index.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  Box,
  Button,
  Backdrop,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import axios from "axios";

// ======================================================
// LEGACY DEAL OPTIONS
// ======================================================

const DEAL_STATUS_OPTIONS = [
  "New",
  "Contacted",
  "Offered",
  "Invoiced",
  "Paid",
  "Cold",
  "Spam",
  "Cancelled",
  "Partner",
];

const DEAL_SCORE_OPTIONS = [
  { value: "A", label: "A: Hot mau minggu ini" },
  { value: "B", label: "B: Warm mau bulan ini" },
  { value: "C", label: "C: Masih kapan kapan" },
];

const SCORE_REQUIRED_STATUSES = ["Contacted", "Offered", "Invoiced"];

// ======================================================
// V2 SALES ACTION OPTIONS
// ======================================================

const SALES_ACTION_OPTIONS = [
  {
    value: "FOLLOW_UP_AGAIN",
    label: "Follow Up Lagi",
  },
  {
    value: "DROP_TO_NURTURE",
    label: "Drop ke AI / Nurture",
  },
  {
    value: "DROP_BAD",
    label: "Drop Bad Lead",
  },
];

const NEXT_FOLLOW_UP_OPTIONS = [
  { value: 0, label: "Hari ini" },
  { value: 1, label: "Besok" },
  { value: 2, label: "2 hari lagi" },
  { value: 3, label: "3 hari lagi" },
  { value: 4, label: "4 hari lagi" },
  { value: 7, label: "7 hari lagi" },
  { value: 14, label: "14 hari lagi" },
];

const NURTURE_DROP_REASONS = [
  {
    value: "NO_REPLY_AFTER_FU",
    label: "Tidak balas setelah beberapa FU",
  },
  {
    value: "NOT_READY_YET",
    label: "Belum siap",
  },
  {
    value: "PRICE_TOO_HIGH",
    label: "Harga terlalu tinggi",
  },
  {
    value: "STILL_CONSIDERING",
    label: "Masih pertimbangan",
  },
  {
    value: "NEED_LATER",
    label: "Butuh nanti",
  },
  {
    value: "WAITING_FOR_PARTNER",
    label: "Menunggu partner/boss",
  },
  {
    value: "BUDGET_NOT_READY",
    label: "Budget belum siap",
  },
  {
    value: "WRONG_TIMING",
    label: "Timing belum pas",
  },
];

const BAD_DROP_REASONS = [
  {
    value: "SPAM_GAJELAS",
    label: "Spam / gajelas",
  },
  {
    value: "ASKING_FOR_MONEY",
    label: "Minta uang / pinjam uang",
  },
  {
    value: "WRONG_NUMBER",
    label: "Nomor salah",
  },
  {
    value: "JOB_SEEKER",
    label: "Cari kerja",
  },
  {
    value: "PARTNERSHIP_ONLY",
    label: "Partnership saja",
  },
  {
    value: "COMPETITOR",
    label: "Kompetitor",
  },
  {
    value: "NOT_RELEVANT",
    label: "Tidak relevan",
  },
  {
    value: "ABUSIVE",
    label: "Kasar / abusive",
  },
];

// cache notes selama page belum refresh
const localDraftCache = {};

// ---------------------------------------------------------------------------
// Helper: format tanggal/waktu aktivitas
// ---------------------------------------------------------------------------
const formatActivityDateTime = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const timeFormatter = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Jakarta",
  });
  const timePart = timeFormatter.format(date);

  const dateFormatter = new Intl.DateTimeFormat("id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Jakarta",
  });
  const datePart = dateFormatter.format(date);

  return `Jam ${timePart} ${datePart}`;
};

export default function InquiryPops(props) {
  const {
  open,
  onClose,
  handleClose,
  data,
  inquiry,
  onSave,
  actorId: propActorId,
  draftNote = "",
  onDraftChange,
  forceV2SalesFlow = false,
} = props;

  const close = onClose || handleClose || (() => {});
  const inquiryData = data || inquiry || {};

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const inquiryId = inquiryData?._id || "";
  const currentDeal = inquiryData?.deal || inquiryData?.currentDeal || {};
  const dealId = currentDeal?._id || "";
  const primaryTask = inquiryData?.primaryTask || null;
  const taskId =
    inquiryData?.taskId || primaryTask?._id || inquiryData?.task?._id || "";

  const isV2 =
    forceV2SalesFlow ||
    inquiryData?.pipelineVersion === "v2" ||
    inquiryData?.leadStage === "S3_QUALIFIED" ||
    !!currentDeal?.opportunityStatus;

  const draftKey = taskId || dealId || inquiryId || "unknown";
  const recordInitKey = `${inquiryId || "no_inquiry"}_${
    taskId || "no_task"
  }_${dealId || "no_deal"}`;

  // Header
  const name = inquiryData?.Nama || "-";
  const phone = inquiryData?.Phone || "-";
  const pic =
    inquiryData?.currentOwnerSales ||
    inquiryData?.Sales ||
    currentDeal?.Sales ||
    "-";

  const followUpCounter =
    typeof currentDeal?.salesFollowUpCounter === "number"
      ? currentDeal.salesFollowUpCounter
      : typeof currentDeal?.counterFU === "number"
      ? currentDeal.counterFU
      : 0;

  const leadStage = inquiryData?.leadStage || "";
  const opportunityStatus = currentDeal?.opportunityStatus || "";

  // Activities awal dari backend
  const initialActivities = inquiryData?.activities || [];

  // State internal activities
  const [activitiesState, setActivitiesState] = useState(initialActivities);

  // Common form state
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Legacy form state
  const [dealStatus, setDealStatus] = useState("New");
  const [dealScore, setDealScore] = useState("");

  // V2 form state
  const [salesAction, setSalesAction] = useState("FOLLOW_UP_AGAIN");
  const [nextFollowUpDays, setNextFollowUpDays] = useState(2);
  const [dropReason, setDropReason] = useState("");

  useEffect(() => {
    if (!open) return;

    const cachedDraft = localDraftCache[draftKey] || "";
    const mergedDraft = cachedDraft || draftNote || "";

    setActivitiesState(inquiryData?.activities || []);
    setNoteText(mergedDraft);
    setSaveError("");

    setDealStatus(currentDeal?.status || "New");
    setDealScore(currentDeal?.score || "");

    setSalesAction("FOLLOW_UP_AGAIN");
    setNextFollowUpDays(2);
    setDropReason("");
  }, [open, recordInitKey]);

  const sortedActivities = useMemo(() => {
    if (!activitiesState) return [];

    return [...activitiesState].sort((a, b) => {
      const ta = new Date(a.date || a.createdAt || 0).getTime();
      const tb = new Date(b.date || b.createdAt || 0).getTime();
      return tb - ta;
    });
  }, [activitiesState]);

  const showScoreDropdown = SCORE_REQUIRED_STATUSES.includes(dealStatus);
  const trimmedNote = noteText.trim();
  const isNoteValid = trimmedNote.length >= 5;

  const isLegacyScoreValid = !showScoreDropdown || !!dealScore;

  const isV2FollowUp = salesAction === "FOLLOW_UP_AGAIN";
  const isV2DropToNurture = salesAction === "DROP_TO_NURTURE";
  const isV2DropBad = salesAction === "DROP_BAD";
  const isV2Drop = isV2DropToNurture || isV2DropBad;

  const isNextFollowUpValid =
    !isV2FollowUp ||
    (Number.isFinite(Number(nextFollowUpDays)) &&
      Number(nextFollowUpDays) >= 0);

  const isDropReasonValid = !isV2Drop || !!dropReason;

  const canSaveLegacy =
    !!inquiryId &&
    !!dealStatus &&
    isNoteValid &&
    isLegacyScoreValid &&
    !saving;

  const canSaveV2 =
    !!inquiryId &&
    !!dealId &&
    !!salesAction &&
    isNoteValid &&
    isNextFollowUpValid &&
    isDropReasonValid &&
    !saving;

  const canSave = isV2 ? canSaveV2 : canSaveLegacy;

  const handleWhatsapp = () => {
    if (!phone || phone === "-") return;

    const cleanPhone = phone.replace(/\D/g, "");
    if (!cleanPhone) return;

    window.open(`https://wa.me/${cleanPhone}`, "_blank", "noopener,noreferrer");
  };

  const handleNoteChange = (event) => {
    const value = event.target.value;
    setNoteText(value);

    if (draftKey) {
      localDraftCache[draftKey] = value;
    }

    if (typeof onDraftChange === "function" && draftKey) {
      onDraftChange(draftKey, value);
    }
  };

  const buildPayloadAndEndpoint = () => {
    if (isV2) {
      return {
        endpoint: `${process.env.REACT_APP_BACKEND_URL}/salesOpportunityUpdateV2`,
        payload: {
          taskId: taskId || null,
          inquiryId,
          dealId: dealId || null,
          actorId: propActorId || inquiryData?.Sales || "sales_unknown",
          noteText: trimmedNote,
          salesAction,
          nextFollowUpDays: Number(nextFollowUpDays),
          dropReason: isV2Drop ? dropReason : "",
        },
      };
    }

    return {
      endpoint: `${process.env.REACT_APP_BACKEND_URL}/addInqActivity`,
      payload: {
        taskId: taskId || null,
        inquiryId,
        dealId: dealId || null,
        actorId: propActorId || inquiryData?.Sales || "sales_unknown",
        noteText: trimmedNote,
        dealStatus,
        dealScore,
      },
    };
  };

  const handleSave = async () => {
    if (!canSave) {
      console.log("[InquiryPops] Save blocked", {
        canSave,
        isV2,
        inquiryId,
        taskId,
        dealId,
        salesAction,
        nextFollowUpDays,
        dropReason,
        dealStatus,
        dealScore,
        trimmedNote,
      });
      return;
    }

    const { endpoint, payload } = buildPayloadAndEndpoint();

    console.log("========== [InquiryPops] SAVE START ==========");
    console.log("[InquiryPops] isV2:", isV2);
    console.log("[InquiryPops] inquiryData:", inquiryData);
    console.log("[InquiryPops] payload:", payload);
    console.log("[InquiryPops] endpoint:", endpoint);

    let serverData = null;

    try {
      setSaving(true);
      setSaveError("");

      const resp = await axios.post(endpoint, payload);

      serverData = resp?.data || {};

      console.log("[InquiryPops] axios response status:", resp?.status);
      console.log("[InquiryPops] axios response data:", serverData);

      const newActivity = serverData?.activity || null;

      if (newActivity) {
        setActivitiesState((prev) => [newActivity, ...(prev || [])]);
      }

      if (draftKey) {
        localDraftCache[draftKey] = trimmedNote;
      }
    } catch (err) {
      console.error("========== [InquiryPops] SAVE ERROR ==========");
      console.error("[InquiryPops] full error object:", err);
      console.error("[InquiryPops] err.response?.status:", err?.response?.status);
      console.error("[InquiryPops] err.response?.data:", err?.response?.data);
      console.error("[InquiryPops] err.message:", err?.message);

      const backendMessage =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "Gagal menyimpan aktivitas. Coba lagi.";

      setSaveError(backendMessage);
      setSaving(false);
      return;
    }

    try {
      if (onSave) {
        console.log("[InquiryPops] calling parent onSave...");
        onSave(inquiryData, payload, serverData);
        console.log("[InquiryPops] parent onSave finished");
      }
    } catch (uiErr) {
      console.error("[InquiryPops] Error inside parent onSave callback:", uiErr);
    }

    setSaving(false);
    console.log("========== [InquiryPops] SAVE SUCCESS ==========");
    window.alert("Saved");
    close();
  };

  const renderDesktopHeader = () => (
    <Box sx={{ px: 2, pb: 2 }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Nama
              </Typography>
              <Typography variant="h6">{name}</Typography>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Nomor Telpon
              </Typography>
              <Typography variant="h6">{phone}</Typography>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">
                PIC Saat ini
              </Typography>
              <Typography variant="h6">{pic}</Typography>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Follow Up Counter
              </Typography>
              <Typography variant="h6">{followUpCounter}</Typography>
            </Grid>

            {isV2 && (
              <>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Lead Stage
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {leadStage || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Opportunity
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {opportunityStatus || "-"}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "flex-end" },
          }}
        >
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            onClick={handleWhatsapp}
          >
            WhatsApp
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderMobileHeader = () => (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        Nomor Telpon
      </Typography>
      <Typography variant="h6">{phone}</Typography>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        Follow Up Counter
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {followUpCounter}
      </Typography>

      {isV2 && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Lead Stage
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {leadStage || "-"}
          </Typography>
        </>
      )}
    </Box>
  );

  const renderNotesBox = () => (
    <Box
      sx={{
        border: "1px solid #000",
        borderRadius: 1,
        p: 1.5,
        mb: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Notes
      </Typography>

      <TextField
        multiline
        minRows={isMobile ? 5 : 6}
        fullWidth
        variant="outlined"
        value={noteText}
        onChange={handleNoteChange}
        placeholder="Tulis notes..."
      />

      {!isNoteValid && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", mt: 0.75 }}
        >
          Notes wajib minimal 5 karakter.
        </Typography>
      )}
    </Box>
  );

  const renderLegacyForm = () => (
    <Box>
      {renderNotesBox()}

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5 }}
        >
          Status Deal
        </Typography>

        <Select
          fullWidth
          size="small"
          value={dealStatus}
          onChange={(e) => setDealStatus(e.target.value)}
        >
          {DEAL_STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {showScoreDropdown && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Rating / Score Deal
          </Typography>

          <Select
            fullWidth
            size="small"
            value={dealScore}
            onChange={(e) => setDealScore(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Pilih Rating
            </MenuItem>

            {DEAL_SCORE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>

          {!isLegacyScoreValid && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.75 }}
            >
              Rating wajib dipilih untuk status ini.
            </Typography>
          )}
        </Box>
      )}

      {renderSaveArea()}
    </Box>
  );

  const renderV2Form = () => {
    const reasonOptions = isV2DropToNurture
      ? NURTURE_DROP_REASONS
      : isV2DropBad
      ? BAD_DROP_REASONS
      : [];

    return (
      <Box>
        {renderNotesBox()}

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Sales Action
          </Typography>

          <Select
            fullWidth
            size="small"
            value={salesAction}
            onChange={(e) => {
              setSalesAction(e.target.value);
              setDropReason("");
            }}
          >
            {SALES_ACTION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {isV2FollowUp && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Next Follow Up
            </Typography>

            <Select
              fullWidth
              size="small"
              value={nextFollowUpDays}
              onChange={(e) => setNextFollowUpDays(Number(e.target.value))}
            >
              {NEXT_FOLLOW_UP_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {isV2Drop && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Drop Reason
            </Typography>

            <Select
              fullWidth
              size="small"
              value={dropReason}
              onChange={(e) => setDropReason(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Pilih alasan drop
              </MenuItem>

              {reasonOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>

            {!isDropReasonValid && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.75 }}
              >
                Drop reason wajib dipilih.
              </Typography>
            )}
          </Box>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          Mark Paid hanya dari invoice/payment flow, bukan dari sales popup.
        </Typography>

        {renderSaveArea()}
      </Box>
    );
  };

  const renderSaveArea = () => (
    <>
      {!!saveError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", mb: 1 }}
        >
          {saveError}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: isMobile ? "stretch" : "flex-end",
        }}
      >
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!canSave}
          fullWidth={isMobile}
        >
          {saving ? "Saving..." : "SAVE"}
        </Button>
      </Box>
    </>
  );

  const renderTimeline = () => (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        borderRadius: 1,
        p: 2,
        height: "100%",
        overflowY: "auto",
      }}
    >
      {sortedActivities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Belum ada aktivitas untuk inquiry ini.
        </Typography>
      ) : (
        sortedActivities.map((activityItem, index) => {
          const noteTextActivity =
            activityItem.note || activityItem.text || "-";

          return (
            <Box
              key={activityItem._id || index}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  borderRadius: "50%",
                  border: "2px solid #000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                  mt: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "#000",
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0 }}>
                  {noteTextActivity}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {formatActivityDateTime(
                    activityItem.date || activityItem.createdAt
                  )}
                </Typography>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );

  const renderForm = () => (isV2 ? renderV2Form() : renderLegacyForm());

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth={isMobile ? "sm" : "lg"}
      BackdropProps={{
        sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
          position: "relative",
          width: isMobile ? "80vw" : undefined,
          maxWidth: isMobile ? "80vw" : undefined,
        },
      }}
    >
      <DialogTitle sx={{ p: 1 }}>
        <IconButton onClick={close} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {isMobile ? renderMobileHeader() : renderDesktopHeader()}

        {isMobile ? (
          <Box sx={{ px: 1.5, pb: 2 }}>{renderForm()}</Box>
        ) : (
          <Grid container spacing={3} sx={{ px: 2, pb: 2 }}>
            <Grid item xs={12} md={7}>
              {renderForm()}
            </Grid>

            <Grid item xs={12} md={5}>
              {renderTimeline()}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <Backdrop
        open={saving}
        sx={{
          position: "absolute",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}